import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { useWallet } from '../contexts/WalletContext';
import { useToast } from '../contexts/ToastContext';

interface Match {
  matchId: string;
  title: string;
  sideA: string;
  sideB: string;
  matchTime: string;
  venue: string;
  status: string;
  totalPool: string;
  activeDuels: number;
  creatorAddress?: string;
}

const FinishMatch: React.FC = () => {
  const { matchId } = useParams<{ matchId: string }>();
  const navigate = useNavigate();
  const { address: walletAddress } = useWallet();
  const { showToast } = useToast();
  
  const [match, setMatch] = useState<Match | null>(null);
  const [result, setResult] = useState<'sideA' | 'sideB' | 'draw' | ''>('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadMatch();
  }, [matchId]);

  const loadMatch = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/judge/detail/${matchId}`);
      const data = await response.json();
      
      if (data.success) {
        setMatch(data.data);
      } else {
        setError(data.message || 'Failed to load match');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load match');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!result) {
      setError('Please select a match result');
      return;
    }

    if (!walletAddress) {
      setError('Please connect your wallet');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const response = await fetch('/api/judge/match/finish', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          matchId,
          result,
          judgeAddress: walletAddress
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to finish match');
      }

      const resultText = result === 'sideA' ? match?.sideA : result === 'sideB' ? match?.sideB : 'Draw';
      showToast(
        `Match completed! Result: ${resultText}, Settled: ${data.data.settledDuels} duels, Payout: ${data.data.totalPayout} CHNX`,
        'success'
      );
      
      navigate('/judge');
    } catch (err: any) {
      console.error('Finish match error:', err);
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-xl">Loading...</div>
        </div>
      </Layout>
    );
  }

  if (error && !match) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
          <button
            onClick={() => navigate('/judge')}
            className="mt-4 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Back to Judge Panel
          </button>
        </div>
      </Layout>
    );
  }

  if (!match) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="text-center">Match not found</div>
        </div>
      </Layout>
    );
  }

  const totalPool = parseFloat(match.totalPool || '0');
  const platformFee = totalPool * 0.05;
  const netPayout = totalPool - platformFee;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-6">
          <button
            onClick={() => navigate('/judge')}
            className="text-blue-600 hover:text-blue-800 flex items-center gap-2"
          >
            ← Back to Judge Panel
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold mb-6">🏆 Finish Match</h1>

          {/* Match Info */}
          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">{match.title}</h2>
            <div className="space-y-2 text-gray-700">
              <p>📅 {new Date(match.matchTime).toLocaleString()}</p>
              <p>📍 {match.venue}</p>
              <p>🎮 Active Duels: {match.activeDuels}</p>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Match Result Selection */}
            <div>
              <label className="block text-lg font-medium text-gray-900 mb-4">
                Select Match Result <span className="text-red-500">*</span>
              </label>
              
              <div className="space-y-3">
                {/* Side A Wins */}
                <label className={`flex items-center p-6 border-2 rounded-lg cursor-pointer transition-colors ${
                  result === 'sideA' 
                    ? 'border-green-500 bg-green-50' 
                    : 'border-gray-300 hover:border-green-400'
                }`}>
                  <input
                    type="radio"
                    name="result"
                    value="sideA"
                    checked={result === 'sideA'}
                    onChange={(e) => setResult(e.target.value as 'sideA')}
                    className="w-5 h-5 text-green-600"
                    disabled={submitting}
                  />
                  <div className="ml-4 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">🏆</span>
                      <span className="text-xl font-semibold text-gray-900">{match.sideA} Wins</span>
                    </div>
                  </div>
                </label>

                {/* Side B Wins */}
                <label className={`flex items-center p-6 border-2 rounded-lg cursor-pointer transition-colors ${
                  result === 'sideB' 
                    ? 'border-green-500 bg-green-50' 
                    : 'border-gray-300 hover:border-green-400'
                }`}>
                  <input
                    type="radio"
                    name="result"
                    value="sideB"
                    checked={result === 'sideB'}
                    onChange={(e) => setResult(e.target.value as 'sideB')}
                    className="w-5 h-5 text-green-600"
                    disabled={submitting}
                  />
                  <div className="ml-4 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">🏆</span>
                      <span className="text-xl font-semibold text-gray-900">{match.sideB} Wins</span>
                    </div>
                  </div>
                </label>

                {/* Draw */}
                <label className={`flex items-center p-6 border-2 rounded-lg cursor-pointer transition-colors ${
                  result === 'draw' 
                    ? 'border-yellow-500 bg-yellow-50' 
                    : 'border-gray-300 hover:border-yellow-400'
                }`}>
                  <input
                    type="radio"
                    name="result"
                    value="draw"
                    checked={result === 'draw'}
                    onChange={(e) => setResult(e.target.value as 'draw')}
                    className="w-5 h-5 text-yellow-600"
                    disabled={submitting}
                  />
                  <div className="ml-4 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">🤝</span>
                      <span className="text-xl font-semibold text-gray-900">Draw / Tie</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">Both sides get refunded (minus platform fee)</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Settlement Details */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Settlement Details:</h3>
              <div className="space-y-3">
                <div className="flex justify-between text-base">
                  <span className="text-gray-700">Active Duels:</span>
                  <span className="font-semibold">{match.activeDuels}</span>
                </div>
                <div className="flex justify-between text-base">
                  <span className="text-gray-700">Total Pool:</span>
                  <span className="font-semibold">{totalPool.toFixed(2)} CHNX</span>
                </div>
                <div className="flex justify-between text-base">
                  <span className="text-gray-700">Platform Fee (5%, stays in escrow):</span>
                  <span className="font-semibold text-orange-600">{platformFee.toFixed(2)} CHNX</span>
                </div>
                <div className="flex justify-between pt-3 border-t border-blue-300 text-lg">
                  <span className="text-gray-900 font-semibold">Net Payout:</span>
                  <span className="font-bold text-green-600">{netPayout.toFixed(2)} CHNX</span>
                </div>
              </div>
            </div>

            {/* Warning */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <div className="flex items-start gap-3">
                <span className="text-3xl">⚠️</span>
                <div className="flex-1">
                  <p className="text-base font-semibold text-red-800 mb-2">
                    This action is irreversible!
                  </p>
                  <p className="text-sm text-red-700">
                    Winners will receive their CHNX immediately via HLF Chaincode. 
                    Please make sure you have selected the correct result.
                  </p>
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-4 pt-4">
              <button
                type="button"
                onClick={() => navigate('/judge')}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-lg"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-lg disabled:bg-gray-400 disabled:cursor-not-allowed"
                disabled={submitting || !result}
              >
                {submitting ? 'Processing...' : 'Confirm & Pay'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default FinishMatch;
